import * as React from "react";

import { type FileRecord } from "@/shared/domain/files";
import { EntityIcon, X } from "@/shared/foundation/icons";
import {
  Banner,
  FormSection,
  RobotoButton,
} from "@/shared/foundation/primitives";
import {
  DataTable,
  cellStyles,
  createColumnHelper,
  useTable,
  type ColDef,
  type DataTableLoadingState,
  type ICellRendererParams,
  type DataTableState,
} from "@/shared/foundation/primitives/Table";
import { formatByteSize, pluralize } from "@/shared/text";

import styles from "./CreateSessionPage.module.css";

interface FilesSectionProps {
  files: FileRecord[];
  isLoadingFiles: boolean;
  filesLoadError: Error | null;
  onAddFiles: () => void;
  onRemoveFiles: (fileIds: string[]) => void;
}

/**
 * Hands each row's remove button the form's current `onRemoveFiles`, so the
 * columns can stay a module constant: a column array rebuilt on every render
 * would make AG Grid re-define its columns each time.
 */
const RemoveFilesContext = React.createContext<(fileIds: string[]) => void>(
  () => undefined,
);

const columnHelper = createColumnHelper<FileRecord>();

// `<DataTable>`'s default ColDef is `hide: true` (visibility is normally
// preference-driven). This table seeds no preferences, so the explicit
// `hide: false` here is what makes the columns render.
const COLUMNS: ColDef<FileRecord>[] = [
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    flex: 1,
    hide: false,
    sortable: false,
    cellClass: cellStyles.primaryContent,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => (
      <span className={styles.pathCell}>
        <EntityIcon type="file" size={14} />
        <span className={styles.pathText}>{p.value}</span>
      </span>
    ),
  }),
  columnHelper.accessor("size", {
    headerName: "Size",
    minWidth: 120,
    hide: false,
    sortable: false,
    // The type right-aligns the header; the cell class replaces the type's,
    // so it carries the alignment class itself.
    type: "rightAligned",
    cellClass: [cellStyles.secondaryContent, "ag-right-aligned-cell"],
    valueFormatter: (p) => formatByteSize(p.value ?? 0),
  }),
  {
    colId: "remove",
    headerName: "",
    width: 56,
    hide: false,
    sortable: false,
    resizable: false,
    cellRenderer: (p: ICellRendererParams<FileRecord>) =>
      p.data ? <RemoveFileButton file={p.data} /> : null,
  },
];

/**
 * Pulls one file out of the working set (the files this form will attach to
 * the session on submit); it never deletes the file itself.
 */
function RemoveFileButton({ file }: { file: FileRecord }) {
  const onRemoveFiles = React.useContext(RemoveFilesContext);
  return (
    <span className={styles.removeCell}>
      <RobotoButton
        variant="ghost"
        size="sm"
        icon={<X aria-hidden />}
        aria-label={`Remove ${file.relative_path}`}
        onClick={() => onRemoveFiles([file.file_id])}
      />
    </span>
  );
}

export function FilesSection({
  files,
  isLoadingFiles,
  filesLoadError,
  onAddFiles,
  onRemoveFiles,
}: FilesSectionProps) {
  const table = useTable<FileRecord>({ getRowId: (file) => file.file_id });
  const { selection, clearSelection } = table;
  // A file removed from its row stays in the table's selection; count only
  // the selected files still in the working set.
  const inWorkingSet = new Set(files.map((file) => file.file_id));
  const selected = selection.filter((file) => inWorkingSet.has(file.file_id));
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

  // A row's remove button unmounts with its row; hand focus to Add files so a
  // keyboard user keeps their place in the section.
  const addFilesRef = React.useRef<HTMLButtonElement>(null);
  const removeFromRow = (fileIds: string[]) => {
    onRemoveFiles(fileIds);
    addFilesRef.current?.focus();
  };

  return (
    <FormSection
      title="Files"
      hint="The session covers the time window of these files."
    >
      <div className={styles.filesToolbar}>
        {files.length > 0 && (
          <span className={styles.count}>
            {files.length} {pluralize("file", files.length)} ·{" "}
            {formatByteSize(totalBytes)}
          </span>
        )}
        {selected.length > 0 && (
          <RobotoButton
            variant="secondary"
            size="sm"
            onClick={() => {
              onRemoveFiles(selected.map((file) => file.file_id));
              clearSelection();
            }}
          >
            Remove {selected.length} {pluralize("file", selected.length)}
          </RobotoButton>
        )}
        {/* Disabled while the prefill loads: applying the prefill replaces
            the selection wholesale, so files added mid-prefill would be
            silently dropped. */}
        <RobotoButton
          ref={addFilesRef}
          variant="secondary"
          size="sm"
          disabled={isLoadingFiles}
          onClick={onAddFiles}
        >
          Add files
        </RobotoButton>
      </div>
      <RemoveFilesContext.Provider value={removeFromRow}>
        <FilesBody
          table={table}
          files={files}
          isLoadingFiles={isLoadingFiles}
          filesLoadError={filesLoadError}
        />
      </RemoveFilesContext.Provider>
    </FormSection>
  );
}

function FilesBody({
  table,
  files,
  isLoadingFiles,
  filesLoadError,
}: {
  table: DataTableState<FileRecord>;
  files: FileRecord[];
  isLoadingFiles: boolean;
  filesLoadError: Error | null;
}) {
  // The working set is fully in memory, so paginate client-side by slicing
  // the current page out of `files`.
  const { currentPage, pageSize, resetCurrentPage } = table;
  const pageStart = currentPage * pageSize;
  const pageRows = files.slice(pageStart, pageStart + pageSize);
  const hasNextPage = pageStart + pageSize < files.length;

  // Removing files can strand the viewer on a now-empty trailing page; snap
  // back to the first page when the current one falls past the end.
  React.useEffect(
    function clampPageToBounds() {
      if (currentPage > 0 && pageStart >= files.length) {
        resetCurrentPage();
      }
    },
    [currentPage, pageStart, files.length, resetCurrentPage],
  );

  if (filesLoadError !== null) {
    return (
      <Banner
        variant="error"
        text={`Failed to load files: ${filesLoadError.message}`}
      />
    );
  }

  const loading: DataTableLoadingState = isLoadingFiles ? "rowsOnly" : "none";

  return (
    <div className={styles.filesGrid}>
      <DataTable<FileRecord>
        table={table}
        columns={COLUMNS}
        data={pageRows}
        loading={loading}
        hasNextPage={hasNextPage}
        totalRowCount={files.length}
        selectionEnabled
        hidePaginationWhenShort
        noRowsContent="No files selected — this session will be created with no associated files. Use Add files to associate files with it."
      />
    </div>
  );
}
